import { formatDistanceToNow } from 'date-fns';
import { enGB } from 'date-fns/locale/en-GB';
import { hu } from 'date-fns/locale/hu';
// Időzóna-tudatos dátum-idő formázás. FONTOS: a dátumokat az Odin/DB-ben UTC-instant-ként tároljuk (a Prisma
// `timestamp` UTC-t ír/olvas; a válasz ISO-`Z`-vel jön), a felületen pedig a FELHASZNÁLÓ időzónája szerint
// jelenítjük meg — alapból `Europe/Budapest`. A profil-beállítás (majd) felülírja ezt; addig a default.

/** A megjelenítési időzóna alapértéke (a profil-beállítás bevezetéséig mindenütt ez). */
export const DEFAULT_TIME_ZONE = 'Europe/Budapest';

/** A UI-nyelvből a formázási locale (magyar → `hu-HU`, egyébként `en-GB`). */
export function localeFor(language: string | undefined): string {
  return (language ?? '').toLowerCase().startsWith('en') ? 'en-GB' : 'hu-HU';
}

/**
 * UTC ISO-instant → lokalizált, IDŐZÓNA-tudatos dátum-idő string (a `timeZone` opción keresztül a `toLocaleString`
 * a helyes zónára konvertál). Érvénytelen bemenet → üres string.
 */
export function formatDateTime(
  iso: string | null | undefined,
  language: string,
  timeZone: string = DEFAULT_TIME_ZONE,
  opts: Intl.DateTimeFormatOptions = { dateStyle: 'short', timeStyle: 'medium' },
): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(localeFor(language), { ...opts, timeZone });
}

/**
 * A megjelenítési időzóna hook-ja — EGY seam az egész app számára. MA: a default (`Europe/Budapest`). KÉSŐBB:
 * a me/profil `timezone` beállításából olvassa (akkor CSAK ezt a hookot kell bekötni, a fogyasztók változatlanok).
 */
export function useTimeZone(): string {
  return DEFAULT_TIME_ZONE;
}

/**
 * ISO-instant → RELATÍV idő („3 perccel ezelőtt"), a UI-nyelv locale-jával. Érvénytelen dátum → üres string.
 *
 * MIÉRT KÖZÖS: a listákban a partnert az érdekli, MENNYIRE friss az esemény, nem a pontos időpont — a
 * pontos érték a részleten van. A helper korábban az értesítés-panel fájljában élt lokálisan; a
 * partner-portál jegy-listája a MÁSODIK használat, tehát a repo DRY-szabálya szerint (CLAUDE.md 4.
 * főszabály) a közös rétegbe került.
 */
export function formatRelativeTime(iso: string, language: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const locale = language.toLowerCase().startsWith('en') ? enGB : hu;
  return formatDistanceToNow(d, { addSuffix: true, locale });
}

/** A mai nap `YYYY-MM-DD` alakban a megjelenítési zónában (az `en-CA` locale pontosan ezt a formát adja). */
export function todayInZone(timeZone: string = DEFAULT_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * NAP-PONTOS különbség a mai naptól: `+N` = N nap múlva, `-N` = N napja volt, `0` = ma. A bemenet
 * `YYYY-MM-DD` (nap-pontos adat), és ZÓNA-KONVERZIÓ NÉLKÜL számol — a nap maga az adat (számla-határidő,
 * lejárat). A „ma" viszont a MEGJELENÍTÉSI zónában (`Europe/Budapest`) értendő, különben éjfél körül egy
 * budapesti felhasználó UTC szerinti napot látna. Érvénytelen bemenet → `null`.
 */
export function daysUntilDay(day: string, timeZone: string = DEFAULT_TIME_ZONE): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const today = todayInZone(timeZone);
  const a = Date.parse(`${today}T00:00:00Z`);
  const b = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

/**
 * ISO (nap-pontos vagy instant) → LOKALIZÁLT RÖVID DÁTUM. Érvénytelen/üres bemenet → üres string (a hívó
 * dönt az em-dash-ről). A `locale` a UI nyelve (`i18n.language`), nem `localeFor()`-ral szűrt érték — a
 * lista-oldalak így hívják.
 *
 * MIÉRT KÖZÖS: ez a helper HÁROM lista-/részlet-fájlban élt bájtra azonos másolatban (számla-lista,
 * számla-részlet, üzenetek), és a kiadás-lista lett volna a NEGYEDIK. A repo DRY-szabálya a 2.
 * előfordulásnál a közös rétegbe rendeli — itt a tét nem csak a sorok száma: a dátum-alak
 * FELÜLETEK KÖZT hasonlítható adat, és négy másolat négyfelé tud elcsúszni.
 *
 * ZÓNA-KONVERZIÓ NINCS: a bemenet jellemzően `YYYY-MM-DD` (számla-kiállítás, határidő), ahol a nap MAGA az
 * adat — ugyanaz az elv, mint a `daysUntilDay`-nél.
 */
export function formatDay(iso: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(locale);
}
